"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Spec = void 0;
const luxon_1 = require("luxon");
const data_1 = require("./data");
const utils_1 = require("./utils");
class Spec {
    constructor(spec) {
        this.type = spec['@type'];
        this.key = spec.key;
        this.label = spec.label || spec.title;
        this.labelWithBilan = spec.labelWithBilan || false;
        this.textJonction = spec.textJonction;
        this.value = spec.value || null;
        this.valueBis = spec.valueBis || null;
        this.valueUnit = spec.valueUnit;
        this.unit = spec.unit;
        this.color = spec.color;
        this.colorWithPerm = spec.colorWithPerm;
        this.options = spec.options;
        this.scope = spec.scope || [];
        this.permission = spec.permission;
        this.contentInverted = spec.contentInverted;
        this.edit = spec.edit || '';
        this.webDisplay = spec.webDisplay || null;
        this.template = spec.template || [];
        this.gridRow = spec.gridRow;
    }
    getLabel(currentUser) {
        return typeof this.label === 'function' ? this.label(currentUser) : this.label;
    }
    hasPerm(currentUser) {
        if (this.permission) {
            for (const perm of this.permission) {
                if (perm[0] === '!' && (0, utils_1.hasPerm)(currentUser, perm.substring(1)))
                    return false;
                if ((0, utils_1.hasPerm)(currentUser, perm) || (perm[0] === '!' && !(0, utils_1.hasPerm)(currentUser, perm.substring(1))))
                    return true;
            }
            return false;
        }
        return true;
    }
    hasScope(bilan) {
        if (this.scope &&
            this.scope.includes('puy_du_fou') &&
            (!data_1.SCOPE_PUY_DU_FOU.includes(bilan.intervention.author_organization_id) ||
                !data_1.SCOPE_PUY_DU_FOU.includes(bilan.author_organization_id)))
            return false;
        return true;
    }
    uuidStringFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const valueLabel = data_1.UUID_OPTIONS.get(value);
        const content = utils_1.emptyishTab.includes(value) || !valueLabel || value.length === 0 ? '<span>--</span>' : valueLabel;
        return {
            key: this.key,
            type: this.type,
            label: this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            isInDanger: !utils_1.emptyishTab.includes(value) || true,
            emptyish: content === '<span>--</span>',
            gridRow: this.gridRow,
        };
    }
    uuidArrayFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const valuesLabels = [];
        value.forEach(e => {
            const temp = data_1.UUID_OPTIONS.get(e);
            if (temp)
                valuesLabels.push(temp);
        });
        const content = utils_1.emptyishTab.includes(value) || value.length === 0 || valuesLabels.length === 0
            ? '<span>--</span>'
            : valuesLabels.join(', ');
        return {
            key: this.key,
            type: this.type,
            label: this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            isInDanger: !utils_1.emptyishTab.includes(value) || true,
            emptyish: content === '<span>--</span>',
            gridRow: this.gridRow,
        };
    }
    linkFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const content = utils_1.emptyishTab.includes(value) || value.length === 0 ? '<span>--</span>' : value;
        return {
            key: this.key,
            type: this.type,
            label: this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            isInDanger: !utils_1.emptyishTab.includes(value) || true,
            emptyish: content === '<span>--</span>',
            gridRow: this.gridRow,
        };
    }
    textFormat(bilan, currentUser) {
        // Permet la jointure pour les listes paramétrées
        const value = this.value(bilan)?.split('@#').join(', ') ?? null;
        // Permet l'affichage de la mise en page tablette si retour à la ligne
        const content = utils_1.emptyishTab.includes(value) || value.length === 0
            ? null
            : value.includes('\n')
                ? `<span>${value.split('\n').map(e => `${e}<br />`)}</span>`
                : `<span>${value}</span>`;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            isInDanger: !utils_1.emptyishTab.includes(value) || true,
            emptyish: content === null,
            gridRow: this.gridRow,
        };
    }
    tristateFormat(bilan, currentUser) {
        const value = this.value(bilan) ?? 'u';
        const content = (0, utils_1.tristateValue)(value, this.contentInverted);
        if (this.colorWithPerm !== undefined)
            this.color = this.colorWithPerm(currentUser);
        let color = 'blue';
        let isInDanger = false;
        if ((this.color === 'green' && content === 'Oui') || (this.color === 'red' && content === 'Non')) {
            color = 'green';
        }
        else if ((this.color === 'green' && content === 'Non') || (this.color === 'red' && content === 'Oui')) {
            color = 'red';
            isInDanger = true;
        }
        else if (content === 'NR') {
            color = 'orange';
        }
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            color,
            isInDanger,
            emptyish: content === 'NR',
            gridRow: this.gridRow,
        };
    }
    integerFormat(bilan, currentUser) {
        const value = this.value(bilan) ?? null;
        const color = typeof this.color === 'function' ? this.color(bilan) : '';
        const unit = this.valueUnit !== undefined
            ? typeof this.valueUnit === 'function'
                ? this.valueUnit(bilan)
                : this.valueUnit
            : typeof this.unit === 'function'
                ? this.unit(currentUser)
                : this.unit;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content: value === null ? null : `${value}${unit ? ` ${unit}` : ''}`,
            unit: unit ?? null,
            color,
            isInDanger: !utils_1.emptyishTab.includes(value),
            emptyish: value === null || value === undefined || isNaN(value),
            gridRow: this.gridRow,
        };
    }
    arrayFormat(bilan, currentUser) {
        const value = this.value(bilan);
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            isInDanger: !utils_1.emptyishTab.includes(value),
            emptyish: value === null || value === undefined || value.length === 0,
            template: this.template,
        };
    }
    floatTimeFormat(bilan, currentUser) {
        const value = this.value(bilan) ?? null;
        const content = (0, utils_1.convertNumToMinSec)(value);
        const color = typeof this.color === 'function' ? this.color(bilan) : '';
        const unit = typeof this.unit === 'function' ? this.unit(currentUser) : this.unit;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value: isNaN(value) || value === -1 || value === 0 ? null : value,
            content,
            unit,
            color,
            emptyish: value === null || value === undefined || isNaN(value) || value === -1 || value === 0,
            gridRow: this.gridRow,
        };
    }
    selectFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const options = typeof this.options === 'function' ? this.options(bilan) : this.options;
        if (value === null)
            return {
                key: this.key,
                type: this.type,
                hasPerm: this.hasPerm(currentUser),
                hasScope: this.hasScope(bilan),
                label: this.getLabel(currentUser),
                emptyish: true,
                value: null,
                content: null,
            };
        const option = options.find(opt => opt.value === value);
        const content = option?.label ?? null;
        let color = 'blue';
        let isInDanger = false;
        if (option?.kind === 'negative') {
            color = 'red';
            isInDanger = true;
        }
        else if (option?.kind === 'positive') {
            color = 'green';
        }
        else if (option?.kind === 'intermediate') {
            color = 'orange';
            isInDanger = true;
        }
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value: content && content !== 'NR' ? value : null,
            content: content && content !== 'NR' ? content : null,
            color,
            isInDanger,
            emptyish: option === undefined ||
                option?.label === null ||
                option?.label === undefined ||
                option?.label === '' ||
                option?.label === 'NR',
            gridRow: this.gridRow,
        };
    }
    selectDoubleFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const valueBis = this.valueBis(bilan);
        const options = typeof this.options === 'function' ? this.options(bilan) : this.options;
        if (value === null || valueBis === null)
            return {
                key: this.key,
                type: this.type,
                hasPerm: this.hasPerm(currentUser),
                hasScope: this.hasScope(bilan),
                label: this.getLabel(currentUser),
                value: null,
                valueBis: null,
                content: null,
                contentLeft: null,
                contentRight: null,
                emptyish: true,
            };
        const optionLeft = options.find(opt => opt.value === value) ?? null;
        const optionRight = options.find(opt => opt.value === valueBis) ?? null;
        const contentLeft = optionLeft?.label ?? null;
        const contentRight = optionRight?.label ?? null;
        let colorLeft = 'blue';
        let colorRight = 'blue';
        let isInDanger = false;
        if (optionLeft?.kind === 'negative') {
            colorLeft = 'red';
            isInDanger = true;
        }
        else if (optionLeft?.kind === 'positive') {
            colorLeft = 'green';
        }
        else if (optionLeft?.kind === 'intermediate') {
            colorLeft = 'orange';
            isInDanger = true;
        }
        if (optionRight?.kind === 'negative') {
            colorRight = 'red';
            isInDanger = true;
        }
        else if (optionRight?.kind === 'positive') {
            colorRight = 'green';
        }
        else if (optionRight?.kind === 'intermediate') {
            colorRight = 'orange';
            isInDanger = true;
        }
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            textJonction: this.textJonction,
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            valueBis,
            content: null,
            contentLeft,
            contentRight,
            colorLeft,
            colorRight,
            isInDanger,
            emptyish: optionLeft === null ||
                optionLeft?.label === null ||
                optionLeft?.label === undefined ||
                optionLeft?.label === '' ||
                optionLeft?.label === 'NR' ||
                optionRight === null ||
                optionRight?.label === null ||
                optionRight?.label === undefined ||
                optionRight?.label === '' ||
                optionRight?.label === 'NR',
            gridRow: this.gridRow,
        };
    }
    selectEditableFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const options = typeof this.options === 'function' ? this.options(bilan) : this.options;
        const option = options.find(opt => opt.value === value);
        const content = option?.label;
        const color = option?.kind === 'negative'
            ? 'red'
            : option?.kind === 'positive'
                ? 'green'
                : option?.kind === 'intermediate'
                    ? 'orange'
                    : 'blue';
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            edit: this.edit,
            color,
            options: this.options,
            emptyish: option === undefined ||
                option?.label === null ||
                option?.label === undefined ||
                option?.label === '' ||
                option?.label === 'NR',
            gridRow: this.gridRow,
        };
    }
    multiselectFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const multiselect = (0, utils_1.multiselect2object)(value, this.options);
        const isInDanger = multiselect?.some(i => i.color === 'red' || i.color === 'orange') || false;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value: multiselect === null ? null : value,
            contents: multiselect,
            isInDanger,
            emptyish: multiselect === null || value === -1,
            gridRow: this.gridRow,
        };
    }
    multitristateFormat(bilan, currentUser) {
        const options = typeof this.options === 'function' ? this.options(bilan) : this.options;
        const multitristate = (0, utils_1.multitristate2object)(options);
        const isInDanger = multitristate?.some(i => i.color === 'red' || i.color === 'orange') || false;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            contents: multitristate,
            isInDanger,
            emptyish: multitristate === null,
            gridRow: this.gridRow,
        };
    }
    multiselectDoubleFormat(bilan, currentUser) {
        const value = this.value(bilan);
        const valueBis = this.valueBis(bilan);
        if (value === null || valueBis === null)
            return {
                key: this.key,
                type: this.type,
                hasPerm: this.hasPerm(currentUser),
                hasScope: this.hasScope(bilan),
                label: this.getLabel(currentUser),
                emptyish: true,
            };
        const multiselect = (0, utils_1.multiselect2object)(value, this.options);
        const multiselectBis = (0, utils_1.multiselect2object)(valueBis, this.options);
        const isInDanger = multiselect?.some(i => i.color === 'red' || i.color === 'orange') ||
            multiselectBis?.some(i => i.color === 'red' || i.color === 'orange') ||
            false;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            textJonction: this.textJonction,
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            contentsLeft: multiselect,
            contentsRight: multiselectBis,
            isInDanger,
            emptyish: multiselect === null || multiselectBis === null || value === -1 || valueBis === -1,
            gridRow: this.gridRow,
        };
    }
    timeFormat(bilan, currentUser) {
        const value = this.value(bilan) === 'null' ? null : this.value(bilan) ?? null;
        const t = luxon_1.DateTime.fromISO(value).setZone('Europe/Paris').setLocale('fr-fr');
        const content = t.isValid
            ? `à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
            : value === null
                ? '--:--'
                : 'Heure invalide';
        const contentTimeOnly = t.isValid
            ? `${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
            : value === null ? '--:--' : 'Heure invalide';
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            contentTimeOnly,
            isInDanger: content !== '--:--' && content !== 'Heure invalide',
            emptyish: content === '--:--' || content === 'Heure invalide',
            gridRow: this.gridRow,
        };
    }
    dateFormat(bilan, currentUser) {
        const value = this.value(bilan) ?? null;
        const t = luxon_1.DateTime.fromISO(value).setZone('Europe/Paris').setLocale('fr-fr');
        const content = t.isValid
            ? `le ${t.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
            : value === null
                ? '--/--/--'
                : 'Date invalide';
        const contentDateOnly = t.isValid
            ? `${t.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
            : value === null ? '--/--/--' : 'Date invalide';
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            contentDateOnly,
            isInDanger: content !== '--/--/--' && content !== 'Date invalide',
            emptyish: content === '--/--/--' || content === 'Date invalide',
            gridRow: this.gridRow,
        };
    }
    datetimeFormat(bilan, currentUser) {
        const value = this.value(bilan);
        value.date = value.date ? value.date : null;
        value.time = value.time ? value.time : null;
        const t = luxon_1.DateTime.fromISO(value.time).setZone('Europe/Paris').setLocale('fr-fr');
        const d = luxon_1.DateTime.fromISO(value.date).setZone('Europe/Paris').setLocale('fr-fr');
        const content = t.isValid && d.isValid
            ? `le ${d.toLocaleString(luxon_1.DateTime.DATE_SHORT)} à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
            : t.isValid && !d.isValid
                ? `à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
                : !t.isValid && d.isValid
                    ? `le ${d.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
                    : value.time === null && value.date === null
                        ? 'le --/--/---- à --:--'
                        : 'Date invalide';
        const contentDateTimeOnly = t.isValid && d.isValid
            ? `${d.toLocaleString(luxon_1.DateTime.DATE_SHORT)} à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
            : t.isValid && !d.isValid
                ? `${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
                : !t.isValid && d.isValid
                    ? `${d.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
                    : value.time === null && value.date === null
                        ? '--/--/---- --:--'
                        : 'Date invalide';
        const contentDate = d.isValid
            ? `le ${d.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
            : value.date === null ? '--/--/--' : 'Date invalide';
        const contentTime = t.isValid
            ? `à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
            : value.time === null ? '--:--' : 'Heure invalide';
        const contentDateOnly = d.isValid
            ? `${d.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
            : value.date === null ? '--/--/--' : 'Date invalide';
        const contentTimeOnly = t.isValid
            ? `${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
            : value.time === null ? '--:--' : 'Heure invalide';
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            content,
            contentDate,
            contentTime,
            contentDateTimeOnly,
            contentDateOnly,
            contentTimeOnly,
            isInDanger: content !== 'le --/--/---- à --:--' && content !== 'Date invalide',
            emptyish: content === 'le --/--/---- à --:--' || content === 'Date invalide',
            gridRow: this.gridRow,
        };
    }
    datetimeV2Format(bilan, currentUser) {
        const value = this.value(bilan)?.split('_')[1];
        const format = this.value(bilan)?.split('_')[0];
        let content = "";
        if (!value)
            content = 'Aucune date';
        else {
            const t = luxon_1.DateTime.fromISO(value).setZone('Europe/Paris').setLocale('fr-fr');
            content =
                t.isValid && format === 'DATETIME'
                    ? `le ${t.toLocaleString(luxon_1.DateTime.DATE_SHORT)} à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
                    : t.isValid && format === 'TIME'
                        ? `à ${t.toLocaleString(luxon_1.DateTime.TIME_24_SIMPLE)}`
                        : t.isValid && format === 'DATE'
                            ? `le ${t.toLocaleString(luxon_1.DateTime.DATE_SHORT)}`
                            : t.isValid && !['DATE', 'TIME', 'DATETIME'].includes(format)
                                ? 'Format invalide'
                                : 'Date invalide';
        }
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value: content === 'Format invalide' || content === 'Date invalide' ? null : value,
            content: content === 'Format invalide' || content === 'Date invalide' ? 'le --/--/---- à --:--' : content,
            isInDanger: content !== 'Format invalide' && content !== 'Date invalide',
            emptyish: content === 'Format invalide' || content === 'Date invalide',
            gridRow: this.gridRow,
        };
    }
    categoryFormat(currentUser) {
        return {
            key: this.key,
            type: this.type,
            label: this.getLabel(currentUser),
            hasPerm: this.hasPerm(currentUser),
            hasScope: true,
            gridRow: this.gridRow,
        };
    }
    surveillanceFormat(bilan, currentUser, surv) {
        const value = this.value(surv);
        const unit = typeof this.unit === 'function' ? this.unit(currentUser) : this.unit;
        const valueUnit = typeof this.valueUnit === 'function' ? this.valueUnit(surv) : this.valueUnit;
        return {
            key: this.key,
            type: this.type,
            label: this.labelWithBilan ? this.getLabel(bilan) : this.getLabel(currentUser),
            unit,
            hasPerm: this.hasPerm(currentUser),
            hasScope: this.hasScope(bilan),
            value,
            valueUnit,
            content: `${value}${unit ? ` ${unit}` : ''}`,
            emptyish: value === null,
            webDisplay: this.webDisplay,
        };
    }
    getHead(currentUser) {
        const unit = typeof this.unit === 'function' ? this.unit(currentUser) : this.unit;
        return {
            label: this.getLabel(currentUser),
            unit,
        };
    }
    getValue(bilan, currentUser, surv = undefined) {
        switch (this.type) {
            case 'uuid-string':
                return this.uuidStringFormat(bilan, currentUser);
            case 'uuid-array':
                return this.uuidArrayFormat(bilan, currentUser);
            case 'link':
                return this.linkFormat(bilan, currentUser);
            case 'text':
                return this.textFormat(bilan, currentUser);
            case 'tristate':
                return this.tristateFormat(bilan, currentUser);
            case 'integer':
                return this.integerFormat(bilan, currentUser);
            case 'array':
            case 'table':
                return this.arrayFormat(bilan, currentUser);
            case 'floatTime':
                return this.floatTimeFormat(bilan, currentUser);
            case 'select':
                return this.selectFormat(bilan, currentUser);
            case 'selectDouble':
                return this.selectDoubleFormat(bilan, currentUser);
            case 'selectEditable':
                return this.selectEditableFormat(bilan, currentUser);
            case 'multiselect':
                return this.multiselectFormat(bilan, currentUser);
            case 'multitristate':
                return this.multitristateFormat(bilan, currentUser);
            case 'multiselectDouble':
                return this.multiselectDoubleFormat(bilan, currentUser);
            case 'time':
                return this.timeFormat(bilan, currentUser);
            case 'date':
                return this.dateFormat(bilan, currentUser);
            case 'dateTime':
                return this.datetimeFormat(bilan, currentUser);
            case 'dateTimeV2':
                return this.datetimeV2Format(bilan, currentUser);
            case 'category':
                return this.categoryFormat(currentUser);
            case 'surveillance':
                return this.surveillanceFormat(bilan, currentUser, surv);
            default:
                return null;
        }
    }
}
exports.Spec = Spec;
